import { Particle, ParticleContainer, Texture } from "pixi.js";

import { Colors } from "../config/DesignConstants";
import Entity from "../core/entity/Entity";
import { hexToRgb, rgbToHex } from "../core/util/ColorUtils";
import { clamp, lerpV2d, polarToVec } from "../core/util/MathUtil";
import { choose, chooseN, rDirection, rUniform } from "../core/util/Random";
import { V, V2d } from "../core/Vector";
import { EARTH_RADIUS } from "./Earth";
import { LevelEntity } from "./LevelEntity";

const DEFAULT_PARTICLE_COUNT = 100;
const DEFAULT_EXPLOSION_SPEED = 150.0;
const PARTICLE_SIZE = 0.35;
const GRAVITY_STRENGTH = 1000;
const EARTH_RADIUS_OFFSET = 5; // Just make gravity a little more friendly
const DAMPING_FACTOR = 0.95; // How much velocity is retained each second
const MIN_PARTICLE_LIFETIME = 2.0; // seconds
const MAX_PARTICLE_LIFETIME = 30.0; // seconds

interface DustParticle {
  particle: Particle; // Pixi Particle for rendering
  velocity: V2d; // Physics velocity (Vector)
  lifetime: number;
  age: number;
}

interface DustExplosionOptions {
  position: V2d;
  particleCount?: number;
  explosionSpeed?: number;
  color?: number;
  colors?: number[];
  polygonVertices?: V2d[];
  direction?: V2d; // Optional direction to bias particles toward
}

export class DustExplosionEffect extends LevelEntity implements Entity {
  sprite: ParticleContainer;

  private particles: DustParticle[] = [];

  constructor({
    position,
    particleCount = DEFAULT_PARTICLE_COUNT,
    explosionSpeed = DEFAULT_EXPLOSION_SPEED,
    color = Colors.ORANGE,
    colors,
    polygonVertices,
    direction,
  }: DustExplosionOptions) {
    super();
    for (let i = 0; i < particleCount; i++) {
      let spawnPosition: V2d;

      // Choose spawn position based on polygon vertices if provided
      if (polygonVertices && polygonVertices.length >= 2) {
        const [vertex1, vertex2] = chooseN(2, polygonVertices);
        spawnPosition = lerpV2d(vertex1, vertex2, rUniform(0, 1));
      } else {
        // Fallback to center explosion (original behavior)
        spawnPosition = V(0, 0);
      }

      let angle: number;
      if (direction) {
        // Bias particles toward the specified direction
        const baseAngle = direction.angle;
        const spreadAngle = Math.PI / 3; // 60 degree spread
        angle = baseAngle + rUniform(-spreadAngle, spreadAngle);
      } else {
        // Random direction (original behavior)
        angle = rDirection();
      }
      const speed = rUniform(0, explosionSpeed);
      const velocity = polarToVec(angle, speed);
      const lifetime = rUniform(MIN_PARTICLE_LIFETIME, MAX_PARTICLE_LIFETIME);

      // Choose color from colors array if provided, otherwise use single color
      const selectedColor: number =
        colors && colors.length > 0 ? choose(...colors) : color;

      // IMPORTANT: ParticleContainer seems to use BGR color format instead of RGB
      // So we need to swap red and blue channels for all colors
      const { r, g, b } = hexToRgb(selectedColor);
      const bgrColor = rgbToHex({ r: b, g, b: r });

      // Create Pixi particle for rendering
      const pixiParticle = new Particle({
        texture: Texture.WHITE, // Use built-in 1x1 white texture
        x: spawnPosition.x,
        y: spawnPosition.y,
        scaleX: PARTICLE_SIZE,
        scaleY: PARTICLE_SIZE,
        alpha: 1,
        tint: bgrColor,
      });

      // pixiParticle.tint = bgrColor;
      // pixiParticle.alpha = 1.0;

      // Store in our data structure
      this.particles.push({
        particle: pixiParticle,
        velocity,
        lifetime: lifetime,
        age: 0,
      });
    }

    // Create ParticleContainer with dynamic position and color
    this.sprite = new ParticleContainer({
      dynamicProperties: {
        position: true, // Updated every frame
        scale: false, // Static
        rotation: false, // Static
        color: true, // For alpha/tint changes
      },
      particles: this.particles.map((p) => p.particle),
    });
    this.sprite.cacheAsTexture(false);
    this.sprite.position.set(position.x, position.y);

    // Pre-calculate expected bounds to avoid clipping issues
    const maxSpeed = explosionSpeed;
    const maxLifetime = MAX_PARTICLE_LIFETIME;
    const maxDistance = maxSpeed * maxLifetime;

    // Set explicit bounds to prevent clipping
    this.sprite.bounds.minX = -maxDistance;
    this.sprite.bounds.minY = -maxDistance;
    this.sprite.bounds.maxX = maxDistance;
    this.sprite.bounds.maxY = maxDistance;
    this.sprite.cullable = false; // Disable culling to eliminate some weird clipping issues
    this.sprite.cullableChildren = false; // Disable culling for children too

    this.sprite.update();
  }

  onTick(dt: number) {
    const effectPosition = this.getPosition();
    const dampingFactor = Math.pow(DAMPING_FACTOR, dt);

    // Update particles in reverse order for safe removal
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const dustParticle = this.particles[i];
      const pixiParticle = dustParticle.particle;
      dustParticle.age += dt;

      const worldPosition = effectPosition.add([
        pixiParticle.x,
        pixiParticle.y,
      ]);

      if (
        // Remove particle if it's past its lifetime
        dustParticle.age >= dustParticle.lifetime ||
        // Or has collided with the Earth
        worldPosition.magnitude < EARTH_RADIUS
      ) {
        this.sprite.removeParticle(pixiParticle);
        this.particles.splice(i, 1);
        continue;
      }

      const gravity = gravityAtPoint(worldPosition).imul(dt);
      dustParticle.velocity.iadd(gravity).imul(dampingFactor);

      const [dx, dy] = dustParticle.velocity.mul(dt);
      pixiParticle.x += dx;
      pixiParticle.y += dy;

      // Update alpha
      const t = dustParticle.age / dustParticle.lifetime;
      pixiParticle.alpha = clamp((1 - t) ** 0.3);
    }

    // Destroy effect when all particles are gone
    if (this.particles.length === 0) {
      this.destroy();
    }
  }
}

/**
 * Calculate gravitational acceleration at a point.
 * a = -G * m / r (it should be r^2 in 3d, but just r feels better in 2d)
 */
function gravityAtPoint(position: V2d): V2d {
  const distance = position.magnitude + EARTH_RADIUS_OFFSET;
  const gravityMagnitude = GRAVITY_STRENGTH / distance;
  return position.normalize(gravityMagnitude).imul(-1);
}
